/**
 * Coalescing scheduler for the live "notifications" domain side effects
 * (operation/batch refetch, protocol-applications reload). Notifications
 * keep being added to the inbox (and can still toast/play a sound)
 * synchronously as they arrive - only the network/Redux side effects are
 * batched into a single window so a burst of live events never fans out
 * into one HTTP request per notification.
 *
 * DELETE_OPERATION never goes through the fetch path: a deleted id is
 * removed from the pending-fetch set (or prevents it from ever being
 * added), regardless of whether the update or the delete was scheduled
 * first within the window.
 */

export const LIVE_SYNC_WINDOW_MS = 250;

const OPERATION_FETCH_TYPES = new Set([
  "NOTE",
  "NEW_OPERATION",
  "UPDATE_OPERATION",
  "START_OPERATION",
  "COMPLETE_SAMPLING",
  "COMPLETE_OPERATION",
  "APPLY_WORKING_PROTOCOL",
  "APPLY_WORKING_PROTOCOL_POST",
  "APPLY_CIP",
  "APPLY_SPARKLING",
  "APPLY_FILTERING",
  "APPLY_STABILIZATION",
]);

const PROTOCOL_REFRESH_TYPES = new Set([
  "COMPLETE_SAMPLING",
  "COMPLETE_OPERATION",
  "DELETE_OPERATION",
  "APPLY_WORKING_PROTOCOL",
  "APPLY_WORKING_PROTOCOL_POST",
  "APPLY_CIP",
  "APPLY_SPARKLING",
  "APPLY_FILTERING",
  "APPLY_STABILIZATION",
]);

const BATCH_TYPES = new Set(["NEW_BATCH", "UPDATE_BATCH"]);

/**
 * @param {Object} deps
 * @param {(id: number) => Promise<Object|null>} deps.fetchOperation
 * @param {(id: number) => Promise<Object|null>} deps.fetchBatch
 * @param {() => Promise<void>} deps.reloadProtocolApplications
 * @param {(op: Object, type: string, notification: Object) => void} deps.dispatchOperationUpdate
 * @param {(id: number|string) => void} deps.dispatchOperationRemoval
 * @param {(batch: Object, type: string) => void} deps.dispatchBatchUpdate
 * @param {(op: Object) => void} [deps.refreshOperationTanks]
 * @param {number} [deps.windowMs]
 */
export const createLiveSyncScheduler = ({
  fetchOperation,
  fetchBatch,
  reloadProtocolApplications,
  dispatchOperationUpdate,
  dispatchOperationRemoval,
  dispatchBatchUpdate,
  refreshOperationTanks,
  windowMs = LIVE_SYNC_WINDOW_MS,
}) => {
  let timer = null;
  let stopped = false;
  let operationEntries = new Map(); // id -> { type, notification }
  let batchEntries = new Map(); // id -> { type }
  let deletedOperationIds = new Set();
  let needsProtocolApplicationsRefresh = false;

  const resolveDeletedIds = (notification, fallbackId) => {
    const explicit = notification?.data?.deleted_operation_ids;
    if (Array.isArray(explicit) && explicit.length > 0) return explicit;
    return fallbackId != null && !Number.isNaN(fallbackId) ? [fallbackId] : [];
  };

  const markDeleted = (id) => {
    if (id == null) return;
    deletedOperationIds.add(id);
    operationEntries.delete(id);
  };

  const scheduleFlush = () => {
    if (stopped || timer) return;
    timer = setTimeout(() => {
      timer = null;
      flush();
    }, windowMs);
  };

  /** Enqueue one live notification for the current (or a new) window. */
  const schedule = (notification) => {
    if (stopped || !notification) return;

    const type = notification?.type;
    const operationId = Number(notification?.data?.operation_id);
    const operationIds = notification?.data?.operation_ids;

    if (PROTOCOL_REFRESH_TYPES.has(type)) needsProtocolApplicationsRefresh = true;

    if (type === "DELETE_OPERATION") {
      resolveDeletedIds(notification, operationId).forEach(markDeleted);
    } else if (Array.isArray(operationIds) && operationIds.length > 0) {
      operationIds
        .map((id) => Number(id))
        .forEach((id) => {
          if (deletedOperationIds.has(id)) return;
          operationEntries.set(id, { type, notification });
        });
    } else if (OPERATION_FETCH_TYPES.has(type) && !Number.isNaN(operationId)) {
      if (!deletedOperationIds.has(operationId)) {
        operationEntries.set(operationId, { type, notification });
      }
    }

    if (BATCH_TYPES.has(type) && notification?.data?.batch_id != null) {
      batchEntries.set(notification.data.batch_id, { type });
    }

    scheduleFlush();
  };

  const flush = async () => {
    const opsToFetch = [...operationEntries.entries()];
    const batchesToFetch = [...batchEntries.entries()];
    const idsToRemove = [...deletedOperationIds];
    const shouldRefreshProtocols = needsProtocolApplicationsRefresh;

    operationEntries = new Map();
    batchEntries = new Map();
    deletedOperationIds = new Set();
    needsProtocolApplicationsRefresh = false;

    // DELETE never fetches - removal is applied directly, once per id.
    idsToRemove.forEach((id) => dispatchOperationRemoval(id));

    for (const [id, { type, notification }] of opsToFetch) {
      if (stopped) return;
      try {
        const op = await fetchOperation(id);
        if (stopped || !op) continue;
        if (type === "NEW_OPERATION" && op.completion_date) continue;
        dispatchOperationUpdate(op, type, notification);
        refreshOperationTanks?.(op);
      } catch (err) {
        console.warn(`Failed to fetch operation ${id} during coalesced live sync`, err);
      }
    }

    for (const [id, { type }] of batchesToFetch) {
      if (stopped) return;
      try {
        const batch = await fetchBatch(id);
        if (stopped || !batch) continue;
        dispatchBatchUpdate(batch, type);
      } catch (err) {
        console.warn(`Failed to fetch batch ${id} during coalesced live sync`, err);
      }
    }

    if (shouldRefreshProtocols && !stopped) {
      try {
        await reloadProtocolApplications();
      } catch (err) {
        console.warn("Failed to reload protocol applications during coalesced live sync", err);
      }
    }
  };

  /** Cancel any pending timer and permanently prevent further flushes. */
  const stop = () => {
    stopped = true;
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  return { schedule, stop };
};
